import { ErrorHandler, Injectable, InjectionToken, inject } from '@angular/core';
import { HttpContext, HttpContextToken, HttpErrorResponse, HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AnalyticsService } from './analytics.service';
import { AnalyticsEventName } from './constants/analytics.constants';
import { traceIdFromRequest } from './correlation/correlation.interceptor';
import { RequestCorrelationService } from './correlation/request-correlation.service';
import { APP_CONTEXT, AppContextEnum } from '@lite-workspace/utils';
import { reportErrorToSentry } from './sentry-error-tracking';
import { normalizeUuidsInUrl } from './analytics-helpers/sanitize-track-payload';

const APP_LABELS: Record<string, string> = {
  [AppContextEnum.ADMIN]: 'fotoclass-app',
  [AppContextEnum.GALLERY]: 'gallery',
};

const ERROR_SHOWN_DEDUP_WINDOW_MS = 60_000;
const errorShownLastEmittedAt: Map<string, number> = new Map();

function shouldEmitErrorShown(dedupKey: string): boolean {
  const now = Date.now();
  const lastAt = errorShownLastEmittedAt.get(dedupKey) ?? 0;
  if (now - lastAt < ERROR_SHOWN_DEDUP_WINDOW_MS) return false;
  errorShownLastEmittedAt.set(dedupKey, now);
  return true;
}

/** Test-only: reset per-session dedup cache. Do not use in production code. */
export function __resetErrorShownDedupForTest(): void {
  errorShownLastEmittedAt.clear();
}

function currentPage(): string {
  return typeof window !== 'undefined' ? window.location.pathname : '';
}

export type ErrorSurface = 'runtime' | 'http' | 'download_archive' | 'generation' | 'upload' | 'auth' | 'image' | 'unknown';

export interface ImageErrorTracker {
  trackImageError(status: number, url: string): void;
}

// Provided only by an app that opens transmission: elsewhere a created AnalyticsService is a closed queue growing in memory.
export const IMAGE_ERROR_TRACKER = new InjectionToken<ImageErrorTracker>('IMAGE_ERROR_TRACKER');

/** Which end of a dead connection the browser could still observe when `http_0` was raised. */
export type NetSide = 'user_offline' | 'page_hidden' | 'ours' | 'user_network' | 'unknown';

const NET_SIDE_PROBE_TIMEOUT_MS = 3_000;

/**
 * Requests whose non-2xx answer is a legitimate reply, not a failure — "import is off" (404),
 * "no session" (401). Set by the CALLER, never by the transport: only the code that knows the
 * question knows the answer is expected; for everyone else that status stays a real error.
 */
export const SKIP_ERROR_TRACKING = new HttpContextToken<boolean>(() => false);

/**
 * The same rule narrowed to the statuses that are answers on THIS request — an upload answering 409
 * «этот файл уже есть» is the server's contract, while a 502 on the very same call is a real failure.
 * A whole-request `SKIP_ERROR_TRACKING` would swallow both.
 */
export const SKIP_ERROR_TRACKING_STATUSES = new HttpContextToken<readonly number[]>(() => []);

function skipsErrorTracking(context: HttpContext, status: number): boolean {
  return context.get(SKIP_ERROR_TRACKING) || context.get(SKIP_ERROR_TRACKING_STATUSES).includes(status);
}

/**
 * `http_0` says only «no answer came back», and the fix differs entirely between a browser that is
 * offline, a tab torn down mid-request and our API being down. The probe asks the app's OWN static
 * root, which keeps answering from the CDN while the API does not — so an answer there means the
 * break is on our side.
 */
async function resolveNetSide(): Promise<NetSide> {
  try {
    if (typeof window === 'undefined') return 'unknown';
    if (window.navigator?.onLine === false) return 'user_offline';
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return 'page_hidden';
    if (typeof fetch !== 'function') return 'unknown';

    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), NET_SIDE_PROBE_TIMEOUT_MS);
    try {
      await fetch(`${window.location.origin}/version.json`, { method: 'HEAD', cache: 'no-store', signal: abort.signal });
      return 'ours';
    } catch {
      return 'user_network';
    } finally {
      clearTimeout(timer);
    }
  } catch {
    return 'unknown';
  }
}

function trackErrorShown(analyticsService: AnalyticsService, properties: Record<string, unknown>): void {
  if (properties['http_status'] !== 0) {
    analyticsService.track(AnalyticsEventName.ERROR_SHOWN, properties);
    return;
  }

  // The event leaves AFTER the probe: nobody is waiting on a connection that has already died.
  void resolveNetSide()
    .then(netSide => analyticsService.track(AnalyticsEventName.ERROR_SHOWN, { ...properties, net_side: netSide }))
    .catch((): undefined => undefined);
}

/**
 * Global Angular ErrorHandler that forwards runtime errors to OpenPanel
 * as `error_shown` events and, when Sentry is configured, the error itself
 * (message + stack) to Sentry. Logs to console to preserve default debugging flow.
 *
 * Per-session dedup: same (error_type, surface, page) tuple won't fire more
 * than once per 60s — protects dashboards from re-render storms.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsErrorHandler implements ErrorHandler {
  private readonly analyticsService = inject(AnalyticsService);
  private readonly appContext = inject(APP_CONTEXT, { optional: true });
  private readonly correlation = inject(RequestCorrelationService);

  handleError(error: unknown): void {
    try {
      // Zone.js wraps unhandled rejections; the real error sits under `rejection`.
      const raw = (error as { rejection?: unknown } | null)?.rejection ?? error;
      const classified = classifyError(raw);
      const page = currentPage();
      const dedupKey = `${classified.error_type}::${classified.surface}::${page}`;
      if (shouldEmitErrorShown(dedupKey)) {
        // OpenPanel gets the classified error class only; message + stack go to our self-hosted
        // Sentry below, scrubbed of emails and query strings (sentry-error-tracking.ts).
        trackErrorShown(this.analyticsService, {
          ...classified,
          page,
          app: this.appContext ? (APP_LABELS[this.appContext] ?? this.appContext) : undefined,
          // An http error's own request is unknown here, and the last request's id would name another one.
          trace_id: raw instanceof HttpErrorResponse ? undefined : (this.correlation.lastTraceId() ?? undefined),
        });
      }
    } catch {
      // Tracking must never break the default error flow
    }

    reportErrorToSentry((error as { rejection?: unknown } | null)?.rejection ?? error);
    console.error(error);
  }

  /**
   * An image that never painted: `<img>` and queue fetches bypass `HttpClient`, so the interceptor
   * never sees them. The caller reports once per exhausted rendition chain; `status` 0 = none seen.
   */
  trackImageError(status: number, url: string): void {
    try {
      const errorType = `image_${status}`;
      const page = currentPage();
      if (!shouldEmitErrorShown(`${errorType}::image::${page}`)) return;
      this.analyticsService.track(AnalyticsEventName.ERROR_SHOWN, {
        error_type: errorType,
        http_status: status,
        // File keys differ per photo; the dashboard groups by the path's shape.
        endpoint: normalizeUuidsInUrl(urlPathOnly(url)),
        surface: 'image' satisfies ErrorSurface,
        page,
        app: this.appContext ? (APP_LABELS[this.appContext] ?? this.appContext) : undefined,
      });
    } catch {
      // Tracking must never break the tile
    }
  }
}

/**
 * HTTP interceptor that fires `error_shown` for any HTTP response with a
 * non-2xx status returned from backend APIs. Re-throws the error unchanged.
 *
 * Surface is inferred from the URL path (download_archive / generation /
 * upload / auth / http fallback). Same per-session 60s dedup as runtime.
 */
export const analyticsHttpErrorInterceptor: HttpInterceptorFn = (req, next): Observable<HttpEvent<unknown>> => {
  const analyticsService = inject(AnalyticsService);
  const appContext = inject(APP_CONTEXT, { optional: true });

  return next(req).pipe(
    catchError(error => {
      if (error instanceof HttpErrorResponse && !skipsErrorTracking(req.context, error.status)) {
        try {
          const surface: ErrorSurface = inferSurfaceFromUrl(req.url);
          const errorType = `http_${error.status}`;
          const page = currentPage();
          const dedupKey = `${errorType}::${surface}::${page}`;
          if (shouldEmitErrorShown(dedupKey)) {
            // Path only — the query string may carry tokens/PII (BIZ-1899 class).
            trackErrorShown(analyticsService, {
              error_type: errorType,
              http_status: error.status,
              endpoint: urlPathOnly(req.url),
              surface,
              page,
              app: appContext ? (APP_LABELS[appContext] ?? appContext) : undefined,
              // No fallback to lastTraceId() here: an http error knows its OWN request, and the last
              // remembered id belongs to a DIFFERENT (usually successful) one — an engineer opening it
              // would see a healthy trace and conclude the failure is not in the backend. An empty key
              // is honest; a wrong one is worse than none.
              trace_id: traceIdFromRequest(req) ?? undefined,
            });
          }
        } catch {
          // ignore tracking failures
        }
      }

      return throwError(() => error);
    }),
  );
};

function urlPathOnly(url: string): string {
  try {
    return new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost').pathname;
  } catch {
    return url.split('?')[0];
  }
}

/**
 * Error class only — never the message, never the object's keys or values (PII).
 * An `HttpErrorResponse` reaching the global handler is classified exactly as the
 * interceptor classifies it, so the 60s dedup swallows the second emit of one response.
 */
function classifyError(error: unknown): {
  error_type: string;
  error_name: string;
  surface: ErrorSurface;
  http_status?: number;
  endpoint?: string;
} {
  if (error instanceof HttpErrorResponse) {
    const url = error.url ?? '';
    return {
      error_type: `http_${error.status}`,
      error_name: 'HttpErrorResponse',
      surface: inferSurfaceFromUrl(url),
      http_status: error.status,
      endpoint: url ? urlPathOnly(url) : undefined,
    };
  }

  const name =
    error instanceof Error
      ? error.name || 'Error'
      : error === null
        ? 'null'
        : typeof error === 'object'
          ? ((error as object).constructor?.name ?? 'Object')
          : typeof error;

  return { error_type: `runtime_${name}`, error_name: name, surface: 'runtime' };
}

function inferSurfaceFromUrl(url: string): ErrorSurface {
  const lower = url.toLowerCase();
  if (lower.includes('/download') || lower.includes('archive')) return 'download_archive';
  if (lower.includes('/photobook') && (lower.includes('/generate') || lower.includes('/regenerate'))) return 'generation';
  if (lower.includes('/upload') || lower.includes('/files') || lower.includes('/photos')) return 'upload';
  if (lower.includes('/auth') || lower.includes('/login') || lower.includes('/otp')) return 'auth';
  return 'http';
}
